/** Só assinatura: ver a nota de `art/canvas.ts` sobre por que o Phaser entra como tipo. */
import type Phaser from 'phaser';
import { Direction, OUTFIT_TIERS, Vocation } from '@game/protocol';

import {
  addGridTexture,
  celLight,
  celMass,
  celShadow,
  clipped,
  context2d,
  createCanvas,
  ellipse,
  mix,
  outline,
  px,
  roundRect,
  roundRectPath,
  type Shape,
} from './canvas.ts';
import {
  EYE,
  EYE_LIGHT,
  OUTLINE,
  SKIN,
  SKIN_SHADE,
  VOCATION_PALETTES,
  type VocationPalette,
} from './palette.ts';

/**
 * Atlas de personagem (PRD §8.1).
 *
 * Quadro de 32×56: o corpo ocupa o tile e a cabeça — com o que estiver sobre ela —
 * transborda para o tile de trás. É o que dá a silhueta anime — cabeça grande, corpo curto
 * — sem inflar a grade de 32×32, que é `TILE_SIZE` e não pode mudar sem invalidar mapa e
 * colisão.
 *
 * ## O que faz este desenho ler como anime, e não como boneco de blocos
 *
 * A primeira versão montava o personagem de `fillRect`: tronco retângulo, braço
 * retângulo, perna retângulo, cabeça de círculo em cima. Cada peça era plana, cada peça
 * tinha o próprio contorno, e o conjunto lia exatamente como o que era — peças
 * encostadas. Quatro decisões mudam isso, e valem também para qualquer arte que entre
 * depois:
 *
 * **Silhueta antes de detalhe.** Nada é retângulo: o tronco é um trapézio de ombro largo
 * e cintura estreita com as quinas moles, os membros são cápsulas, a cabeça é um ovo de
 * crânio largo e queixo fino. O olho lê a silhueta primeiro, e é ela que diz "pessoa".
 *
 * **Um contorno, tirado da silhueta pronta.** Cada quadro é desenhado num canvas próprio
 * e só no fim `outline()` percorre o resultado — a mesma regra que `creatures.ts` já
 * seguia. Contornar peça por peça produz anéis escuros no meio do corpo, que é o que
 * fazia o personagem parecer costurado.
 *
 * **Sombra de borda dura, e fria.** Não há degradê: há uma banda de sombra com contorno
 * definido no lado oposto ao sol, e ela é mais azul que a cor base (`celShadow`). Borda
 * dura é o traço técnico que define cel shading; o desvio de matiz é o que impede que a
 * sombra pareça sujeira sobre a cor.
 *
 * **O rosto é feito de três coisas.** Franja com faixa de brilho, olho grande com luz, e
 * a sombra que a franja joga na testa. Nessa ordem de importância — e a faixa de brilho
 * do cabelo sozinha carrega mais leitura de "anime" que qualquer detalhe do rosto num
 * sprite deste tamanho.
 *
 * A âncora é o centro-inferior, para o personagem "pisar" no tile em vez de flutuar
 * sobre ele.
 *
 * ## Por que a vocação não pode ser só uma cor
 *
 * Durante muito tempo as quatro vocações eram **o mesmo boneco repintado**: o Knight era o
 * Sorcerer em azul. Cor é o último dado que o olho processa e o primeiro que o cenário
 * destrói — dentro de uma caverna, contra grama, à noite, ou para quem não distingue verde
 * de vermelho, um personagem de dezesseis pixels de largura é **só** a sua silhueta. Uma
 * classe que não muda a silhueta não existe visualmente.
 *
 * Então cada vocação muda o **contorno**, e não a paleta (que continua em `palette.ts`):
 *
 * | vocação  | cabeça          | corpo                  | mãos              |
 * |----------|-----------------|------------------------|-------------------|
 * | Knight   | elmo com crista | placa, ombro largo     | espada e escudo   |
 * | Paladin  | gorro com pluma | gibão e talabarte      | arco e aljava     |
 * | Druid    | capuz           | túnica longa e manto   | cajado de folhas  |
 * | Sorcerer | chapéu de bico  | veste longa e gola     | cajado de orbe    |
 *
 * A regra que decide cada linha dessa tabela é a mesma: **um traço que muda a silhueta vale
 * mais que dez que mudam o preenchimento**. O bico do chapéu, o pé do capuz, a crista do
 * elmo e a pluma do gorro se distinguem em quatro pixels de altura, contra qualquer fundo,
 * mesmo com o personagem de costas — que é a direção em que o jogador passa metade do
 * tempo olhando o próprio herói. Detalhe de peito (fivela, emblema, botão) só se lê parado
 * e de frente, e por isso nunca é o que carrega a leitura de classe.
 *
 * A **arma fica na mão o tempo todo**, e não só nos três quadros de ataque. Ela é metade da
 * silhueta de classe — arco e cajado dizem mais de longe que qualquer detalhe da roupa — e
 * uma arma que aparece só no golpe transforma cada ataque num pop visual, com a mão vazia
 * de volta no quadro seguinte.
 *
 * ## Organização dos quadros
 *
 * Uma linha por direção, na mesma ordem de `Direction` — norte, leste, sul, oeste — para
 * que o índice da animação saia da direção sem tabela de tradução. Doze colunas:
 *
 * | colunas | animação   |
 * |---------|------------|
 * | 0–1     | parado     |
 * | 2–5     | caminhada  |
 * | 6–8     | ataque     |
 * | 9–11    | conjuração |
 *
 * A quinta linha é a morte, que não tem direção: um corpo caído fica igual de qualquer
 * ângulo, e gastar quatro linhas nisso seria quadruplicar a textura por nada.
 */

export const FRAME_WIDTH = 32;

/**
 * Altura do quadro: 56, e não 48.
 *
 * Os oito pixels a mais são **só** teto para o que fica acima do crânio. Em 48 a massa de
 * cabelo já encostava na borda de cima, e qualquer chapéu, capuz ou crista nasceria cortado
 * — o que na prática obrigava as quatro classes a andarem de cabeça descoberta, que é a
 * razão de elas terem passado tanto tempo idênticas. O corpo não desceu um pixel: tudo é
 * medido a partir de `GROUND`, então o personagem continua pisando exatamente onde pisava.
 */
export const FRAME_HEIGHT = 56;
export const COLUMNS = 12;
export const ROWS = 5;

/** Linha da morte, sem direção. */
const DEATH_ROW = 4;

const IDLE_FRAMES = [0, 1];
const WALK_FRAMES = [2, 3, 4, 5];
const ATTACK_FRAMES = [6, 7, 8];
const CAST_FRAMES = [9, 10, 11];

export type CharacterAnimation = 'idle' | 'walk' | 'attack' | 'cast' | 'death';

/** Chave da textura de uma vocação numa faixa de equipamento. */
export function characterTextureKey(vocation: number, tier: number): string {
  return `char-${vocation}-${Math.min(tier, OUTFIT_TIERS - 1)}`;
}

export function characterAnimationKey(
  vocation: number,
  tier: number,
  animation: CharacterAnimation,
  direction: Direction,
): string {
  const key = characterTextureKey(vocation, tier);
  return animation === 'death' ? `${key}-death` : `${key}-${animation}-${direction}`;
}

interface Pose {
  /** −1 a 1: pernas alternando na caminhada. */
  legSwing: number;
  /** Deslocamento vertical do tronco. Um pixel basta para a caminhada não parecer deslizar. */
  bob: number;
  /** 0 a 1: o quanto o braço da arma avança. */
  reach: number;
  /** 0 a 1: braços erguidos e orbe de conjuração. */
  cast: number;
  /** 0 a 1: o corpo desabando. */
  fall: number;
}

const REST: Pose = { legSwing: 0, bob: 0, reach: 0, cast: 0, fall: 0 };

/**
 * O que distingue uma vocação da outra no traço.
 *
 * Mora aqui, e não em `palette.ts`, porque não é cor: é **forma**. A paleta responde "de que
 * cor é o Druid"; esta tabela responde "que forma tem o Druid", que é a pergunta que decide
 * se ele é reconhecível a 100% de zoom no meio de uma caçada.
 */
interface Look {
  /** O que cobre a cabeça. É o traço que sobrevive de costas, de longe e contra qualquer fundo. */
  readonly head: 'helm' | 'cap' | 'hood' | 'hat';
  /** Veste longa até o chão em vez de calça: apaga o vão entre as pernas e alarga a base. */
  readonly robe: boolean;
  readonly weapon: 'sword' | 'bow' | 'staff';
  /** Escudo no braço de trás. */
  readonly shield: boolean;
  /** Aljava nas costas, com as flechas passando do ombro. */
  readonly quiver: boolean;
  /** Largura do ombro de frente. Peso de armadura é largura de ombro. */
  readonly shoulder: number;
}

const LOOKS: Readonly<Record<Vocation, Look>> = {
  [Vocation.Knight]: {
    head: 'helm',
    robe: false,
    weapon: 'sword',
    shield: true,
    quiver: false,
    shoulder: 15,
  },
  [Vocation.Paladin]: {
    head: 'cap',
    robe: false,
    weapon: 'bow',
    shield: false,
    quiver: true,
    shoulder: 12.5,
  },
  [Vocation.Druid]: {
    head: 'hood',
    robe: true,
    weapon: 'staff',
    shield: false,
    quiver: false,
    shoulder: 12.5,
  },
  [Vocation.Sorcerer]: {
    head: 'hat',
    robe: true,
    weapon: 'staff',
    shield: false,
    quiver: false,
    shoulder: 11.5,
  },
};

/**
 * Gera as texturas de todas as vocações e faixas.
 *
 * Doze atlas (quatro vocações × três faixas) somam 12 × 384 × 280 px — cerca de cinco megas
 * de textura, gerados em poucos milissegundos na entrada. Gerar sob demanda pouparia
 * memória e traria engasgo no primeiro personagem de cada tipo que entrasse em cena,
 * que é exatamente o momento em que o jogador está prestando atenção.
 */
export function buildCharacterAtlases(scene: Phaser.Scene): void {
  for (const vocation of [
    Vocation.Knight,
    Vocation.Paladin,
    Vocation.Druid,
    Vocation.Sorcerer,
  ]) {
    for (let tier = 0; tier < OUTFIT_TIERS; tier++) {
      buildAtlas(scene, vocation, tier);
    }
  }
}

function buildAtlas(scene: Phaser.Scene, vocation: Vocation, tier: number): void {
  const key = characterTextureKey(vocation, tier);
  if (scene.textures.exists(key)) return;

  addGridTexture(
    scene,
    key,
    characterSheet(vocation, tier),
    FRAME_WIDTH,
    FRAME_HEIGHT,
    COLUMNS,
    ROWS,
  );
  registerAnimations(scene, vocation, tier);
}

/**
 * A folha de sprite de uma vocação, sem passar pelo Phaser.
 *
 * Separada do registro da textura pela mesma razão que `creatureSheet`: desenhar e
 * publicar são coisas diferentes, e assim a arte pode ser gerada e **olhada** fora do
 * jogo — que é como se revisa pixel art sem ter que entrar numa partida para ver o
 * resultado de cada ajuste (ver `characters-preview.html`).
 */
export function characterSheet(vocation: Vocation, tier: number): HTMLCanvasElement {
  const canvas = createCanvas(FRAME_WIDTH * COLUMNS, FRAME_HEIGHT * ROWS);
  const ctx = context2d(canvas);
  const palette = VOCATION_PALETTES[vocation];
  const look = LOOKS[vocation];

  // Quadro de trabalho: o corpo é desenhado aqui sozinho para que `outline()` extraia o
  // contorno da silhueta dele, e não da sombra que ele projeta no chão — sombra
  // contornada vira uma poça com borda, que é o defeito mais visível que um contorno por
  // silhueta pode introduzir.
  const cell = createCanvas(FRAME_WIDTH, FRAME_HEIGHT);
  const cellCtx = context2d(cell, true);

  const blit = (
    column: number,
    row: number,
    direction: Direction,
    pose: Pose,
    alpha: number,
  ): void => {
    cellCtx.clearRect(0, 0, FRAME_WIDTH, FRAME_HEIGHT);
    drawCharacter(cellCtx, direction, palette, look, tier, pose);
    outline(cellCtx, OUTLINE);

    ctx.save();
    ctx.translate(column * FRAME_WIDTH, row * FRAME_HEIGHT);
    ctx.globalAlpha = alpha;
    groundShadow(ctx, pose);
    ctx.drawImage(cell, 0, 0);
    ctx.restore();
  };

  for (const direction of [
    Direction.North,
    Direction.East,
    Direction.South,
    Direction.West,
  ]) {
    for (let column = 0; column < COLUMNS; column++) {
      blit(column, direction, direction, poseFor(column), 1);
    }
  }

  for (let column = 0; column < 4; column++) {
    blit(column, DEATH_ROW, Direction.South, { ...REST, fall: (column + 1) / 4 }, 1 - column * 0.2);
  }

  return canvas;
}

/**
 * Sombra no chão, desenhada na folha e **fora** do quadro de trabalho.
 *
 * Não sobe com o `bob`: sombra que acompanha o pulo do personagem denuncia o truque. Ela
 * encolhe com a queda da morte, porque um corpo caído toca o chão em mais lugares mas
 * projeta menos.
 */
function groundShadow(ctx: CanvasRenderingContext2D, pose: Pose): void {
  const spread = 1 - pose.fall * 0.35;
  ctx.save();
  ctx.globalAlpha *= 0.3;
  ellipse(ctx, FRAME_WIDTH / 2, GROUND, 8 * spread, 2.6 * spread, '#000000');
  ctx.restore();
}

function poseFor(column: number): Pose {
  // Parado: uma respiração de um pixel entre os dois quadros.
  if (column === 0) return REST;
  if (column === 1) return { ...REST, bob: -1 };

  if (column >= 2 && column <= 5) {
    // Caminhada de quatro quadros: passo direito, neutro, passo esquerdo, neutro.
    const swing = [1, 0, -1, 0][column - 2] ?? 0;
    return { ...REST, legSwing: swing, bob: swing === 0 ? 0 : -1 };
  }

  if (column >= 6 && column <= 8) {
    // Ataque: recuo, avanço, retorno. O recuo é o que dá peso ao golpe.
    const reach = [-0.4, 1, 0.5][column - 6] ?? 0;
    return { ...REST, reach };
  }

  const cast = [0.3, 1, 0.6][column - 9] ?? 0;
  return { ...REST, cast };
}

// --- Desenho ----------------------------------------------------------------

/** Linha do chão dentro do quadro: onde o pé encosta. */
const GROUND = FRAME_HEIGHT - 2;

/**
 * Do chão ao topo da arte mais alta — o bico do chapéu do Sorcerer.
 *
 * É daqui que o nome sobe (`render/creature-sprite.ts`). Medido do **conjunto**, e não de
 * cada vocação: se cada classe tivesse a sua, o nome dançaria para cima e para baixo quando
 * dois jogadores de vocações diferentes ficassem lado a lado, e a diferença de altura entre
 * eles é justamente a leitura de silhueta que este arquivo existe para produzir.
 */
export const CHARACTER_ART_HEIGHT = 53;

/** Cápsula: o membro de todo mundo. Retângulo com as pontas redondas. */
function capsule(x: number, y: number, w: number, h: number): Shape {
  return (c) => roundRectPath(c, x, y, w, h, Math.min(w, h) / 2);
}

/**
 * Tronco: trapézio de ombro largo e cintura estreita, com as quinas moles.
 *
 * As duas larguras não são enfeite — são a leitura de "pessoa". Um retângulo de largura
 * constante lê como caixa em qualquer tamanho, e a diferença de três pixels entre ombro e
 * cintura é o que faz a mesma área de pixel virar torso.
 */
function torsoShape(cx: number, top: number, bottom: number, shoulder: number, waist: number): Shape {
  const sh = shoulder / 2;
  const wa = waist / 2;

  return (c) => {
    c.beginPath();
    c.moveTo(cx - sh, top + 1.5);
    c.quadraticCurveTo(cx - sh - 0.5, top + 4, cx - wa, bottom - 1.5);
    c.quadraticCurveTo(cx - wa, bottom, cx - wa + 1.5, bottom);
    c.lineTo(cx + wa - 1.5, bottom);
    c.quadraticCurveTo(cx + wa, bottom, cx + wa, bottom - 1.5);
    c.quadraticCurveTo(cx + sh + 0.5, top + 4, cx + sh, top + 1.5);
    c.quadraticCurveTo(cx + sh, top, cx + sh - 1.5, top);
    c.lineTo(cx - sh + 1.5, top);
    c.quadraticCurveTo(cx - sh, top, cx - sh, top + 1.5);
    c.closePath();
  };
}

/** Crânio largo terminando em queixo fino: a proporção que se lê como rosto de anime. */
function headShape(cx: number, cy: number): Shape {
  return (c) => {
    c.beginPath();
    c.moveTo(cx - 7, cy - 1);
    c.bezierCurveTo(cx - 7.5, cy - 10, cx + 7.5, cy - 10, cx + 7, cy - 1);
    c.bezierCurveTo(cx + 6.5, cy + 3, cx + 3.5, cy + 6.5, cx, cy + 6.5);
    c.bezierCurveTo(cx - 3.5, cy + 6.5, cx - 6.5, cy + 3, cx - 7, cy - 1);
    c.closePath();
  };
}

interface Point {
  x: number;
  y: number;
}

/** Desenha em volta de um ponto, girado. É o que permite a arma acompanhar o golpe. */
function pivoted(
  ctx: CanvasRenderingContext2D,
  at: Point,
  angle: number,
  draw: (c: CanvasRenderingContext2D) => void,
): void {
  ctx.save();
  ctx.translate(at.x, at.y);
  ctx.rotate(angle);
  draw(ctx);
  ctx.restore();
}

function drawCharacter(
  ctx: CanvasRenderingContext2D,
  direction: Direction,
  palette: VocationPalette,
  look: Look,
  tier: number,
  pose: Pose,
): void {
  const cx = FRAME_WIDTH / 2;
  const fall = pose.fall * 12;
  const bob = pose.bob;
  const profile = direction === Direction.East || direction === Direction.West;
  const back = direction === Direction.North;

  const footY = GROUND - 1 + fall;
  const hipY = GROUND - 13 + fall;
  const torsoTop = GROUND - 25 + bob + fall;
  const headCY = GROUND - 34 + bob + fall;

  const arms = armAnchors(cx, torsoTop, look, pose, direction);
  const weaponHand = handOf(arms.front);
  const freeHand = handOf(arms.back);
  // Para onde o personagem aponta: a arma sai da mão nessa direção, e é só isso que separa
  // um golpe para o leste de um golpe para o oeste.
  const facing = direction === Direction.West ? -1 : 1;

  // Manto: atrás do corpo, menos de costas — de costas ele é a única coisa que se vê.
  if (tier >= 2 && !back) {
    drawCloak(ctx, cx, torsoTop, hipY + 6, palette, profile);
  }

  // A aljava é uma peça das **costas**: de frente só as pontas das flechas passam do ombro,
  // e é o tronco desenhado por cima que produz isso sozinho.
  if (look.quiver && !back) drawQuiver(ctx, cx, torsoTop, palette, direction);

  if (look.robe) drawRobe(ctx, cx, hipY - 2, footY, palette, tier, pose, profile);
  else drawLegs(ctx, cx, hipY, footY, palette, pose, profile);

  // De perfil, o braço de trás fica atrás do tronco; de frente e de costas, os dois
  // braços passam à frente. É a única razão de o desenho do braço ser chamado duas vezes.
  if (profile) drawArm(ctx, arms.back, palette, look, tier, true);

  // De costas a arma fica do outro lado do corpo. Desenhada antes do tronco, ela some por
  // trás dele exatamente como sumiria de verdade — e sem isso um cajado de costas parece
  // estar sendo carregado na frente do peito.
  if (back) drawWeapon(ctx, weaponHand, palette, look, tier, pose, facing, profile);
  if (look.shield && profile) drawShield(ctx, freeHand, palette, tier, profile);

  drawTorso(ctx, cx, torsoTop, hipY + 2, palette, look, tier, profile, back);

  if (tier >= 2 && back) drawCloak(ctx, cx, torsoTop, hipY + 6, palette, profile);
  if (look.quiver && back) drawQuiver(ctx, cx, torsoTop, palette, direction);

  if (!profile) drawArm(ctx, arms.back, palette, look, tier, false);
  drawArm(ctx, arms.front, palette, look, tier, false);

  if (look.shield && !profile) drawShield(ctx, freeHand, palette, tier, profile);

  drawHead(ctx, cx, headCY, torsoTop, palette, look, tier, direction);

  if (!back) drawWeapon(ctx, weaponHand, palette, look, tier, pose, facing, profile);
  if (pose.cast > 0) {
    drawCastGlow(ctx, weaponHand, freeHand, palette, look, tier, pose, facing);
  }
}

/**
 * Onde nasce cada braço.
 *
 * Separado do desenho porque a **arma** precisa da mesma medida: espada, arco e cajado saem
 * da mão, e uma mão desenhada num lugar com a arma calculada noutro é o defeito clássico de
 * sprite montado por camadas — a arma flutua a dois pixels do punho e nada mais convence.
 */
function armAnchors(
  cx: number,
  torsoTop: number,
  look: Look,
  pose: Pose,
  direction: Direction,
): { front: Point; back: Point } {
  // Quem tem cajado ergue menos o braço: a haste é mais alta que o personagem, e um braço
  // que sobe oito pixels levaria o orbe para fora do quadro justo no auge da conjuração.
  const raise = pose.cast * (look.weapon === 'staff' ? 4 : 8);
  const forward = pose.reach * 4;
  const top = torsoTop + 2 - raise;

  if (direction === Direction.East) {
    return { front: { x: cx + 2.5 + forward, y: top }, back: { x: cx - 5, y: top + 1 } };
  }
  if (direction === Direction.West) {
    return { front: { x: cx - 6.5 - forward, y: top }, back: { x: cx + 1, y: top + 1 } };
  }
  return { front: { x: cx + 4, y: top - forward }, back: { x: cx - 8, y: top } };
}

/** O centro da mão de um braço ancorado em `anchor`. */
function handOf(anchor: Point): Point {
  return { x: anchor.x + 2, y: anchor.y + 11.6 };
}

function drawLegs(
  ctx: CanvasRenderingContext2D,
  cx: number,
  top: number,
  bottom: number,
  palette: VocationPalette,
  pose: Pose,
  profile: boolean,
): void {
  const height = bottom - top;
  const swing = pose.legSwing * 2.2;

  const leg = (x: number, y: number, h: number, cloth: string): void => {
    celMass(ctx, capsule(x, y, 5, h), cloth, { depth: 0.3 });
    // Bota: mais escura que qualquer sombra da roupa, e um pouco mais larga que a perna.
    // É ela que dá peso ao pé — sem ela a perna termina em ponta e o personagem flutua.
    roundRect(ctx, x - 0.5, y + h - 4.5, 6, 4.5, 1.8, celShadow(cloth, 0.62));
    roundRect(ctx, x - 0.5, y + h - 4.5, 6, 1.2, 0.6, celShadow(cloth, 0.44));
  };

  // De perfil, uma perna na frente da outra; de frente e de costas, lado a lado.
  if (profile) {
    leg(cx - 2.5, top - swing, height, palette.clothShade);
    leg(cx - 2.5, top + swing, height, palette.cloth);
    return;
  }

  // Vão de dois pixels entre as pernas: com um só, o contorno das duas se toca e o par
  // vira uma saia. O vão é onde a silhueta diz que são duas pernas.
  leg(cx - 6, top + Math.max(0, swing), height - Math.abs(swing), palette.cloth);
  leg(cx + 1, top + Math.max(0, -swing), height - Math.abs(swing), palette.clothShade);
}

/**
 * A veste longa dos conjuradores.
 *
 * É a peça de silhueta mais forte do jogo depois do chapéu: onde Knight e Paladin terminam
 * em duas colunas separadas por um vão, Druid e Sorcerer terminam num **trapézio maciço**
 * que abre até o chão. A diferença aparece a qualquer distância, de qualquer ângulo, e
 * sobrevive à noite e dentro da caverna, quando a cor já não diz nada.
 *
 * A barra em três pontas é o que impede a peça de ler como sino: pano tem bainha irregular,
 * e é a irregularidade que `outline()` transforma em recorte.
 */
function drawRobe(
  ctx: CanvasRenderingContext2D,
  cx: number,
  top: number,
  bottom: number,
  palette: VocationPalette,
  tier: number,
  pose: Pose,
  profile: boolean,
): void {
  // A barra balança com o passo, e o balanço é o **dobro** do da perna: pano solto sempre
  // chega depois do corpo, e é esse atraso que faz a veste parecer tecido em vez de casca.
  const sway = pose.legSwing * 2.4;
  const halfTop = profile ? 4.6 : 5.4;
  const halfBottom = profile ? 7 : 9.2;

  // Bota espiando sob a bainha: sem ela a veste encosta no chão como cortina, e a mesma
  // silhueta que devia dizer "mago" passa a dizer "sino".
  const boot = celShadow(palette.clothShade, 0.5);
  roundRect(ctx, cx - 5, bottom - 3.5, 5.5, 4, 1.6, boot);
  if (!profile) roundRect(ctx, cx + 0.5, bottom - 3.5, 5.5, 4, 1.6, boot);

  const shape: Shape = (c) => {
    c.beginPath();
    c.moveTo(cx - halfTop, top);
    c.quadraticCurveTo(cx - halfBottom - 0.5, bottom - 7, cx - halfBottom + sway, bottom - 0.5);
    c.lineTo(cx - halfBottom * 0.42 + sway, bottom - 3);
    c.lineTo(cx + sway * 0.6, bottom - 0.5);
    c.lineTo(cx + halfBottom * 0.42 + sway, bottom - 3);
    c.lineTo(cx + halfBottom + sway, bottom - 0.5);
    c.quadraticCurveTo(cx + halfBottom + 0.5, bottom - 7, cx + halfTop, top);
    c.closePath();
  };

  celMass(ctx, shape, palette.cloth, { depth: 0.34, light: palette.clothLight });

  // Dois vincos descendo do quadril: é o que dá prega ao pano. Recortados à própria veste
  // para que acompanhem o balanço da barra em vez de atravessá-la.
  clipped(ctx, shape, (c) => {
    const fold = celShadow(palette.cloth, 0.28);
    c.save();
    c.strokeStyle = fold;
    c.lineWidth = 1;
    c.beginPath();
    c.moveTo(cx - 2, top + 2);
    c.lineTo(cx - 3.5 + sway, bottom - 1);
    c.moveTo(cx + 2.5, top + 2);
    c.lineTo(cx + 4 + sway, bottom - 1);
    c.stroke();
    c.restore();

    // Barra bordada a partir da segunda faixa: a fita na bainha é a leitura de "veste cara"
    // que não custa nenhum pixel de silhueta.
    if (tier >= 1) {
      const trim = trimOf(palette, tier);
      c.fillStyle = trim;
      c.fillRect(cx - halfBottom - 1 + sway, bottom - 2.6, halfBottom * 2 + 2, 1.2);
    }
  });
}

function drawTorso(
  ctx: CanvasRenderingContext2D,
  cx: number,
  top: number,
  bottom: number,
  palette: VocationPalette,
  look: Look,
  tier: number,
  profile: boolean,
  back: boolean,
): void {
  const shoulder = profile ? look.shoulder - 4 : look.shoulder;
  const waist = profile ? 7.5 : look.robe ? 9 : 9.5;
  const shape = torsoShape(cx, top, bottom, shoulder, waist);
  const trim = trimOf(palette, tier);

  celMass(ctx, shape, palette.cloth, { depth: 0.34, light: palette.clothLight });

  clipped(ctx, shape, (c) => {
    // Cinto: a faixa de equipamento aparece na cor dele. É a leitura mais barata de
    // "este personagem está mais forte" a 100% de zoom. Recortado ao tronco para acompanhar
    // a cintura em vez de atravessá-la reto.
    roundRect(c, cx - 8, bottom - 4, 16, 2.5, 1, celShadow(trim, 0.14));
    roundRect(c, cx - 8, bottom - 4, 16, 1, 0.5, trim);
    // Fivela: o único ponto de luz do cinto. Sem ela a faixa lê como listra de camisa.
    roundRect(c, cx - 1.8, bottom - 4.4, 3.6, 3.4, 1.2, celLight(trim, 0.3));

    if (look.head === 'helm') plateDetail(c, cx, top, bottom, palette, trim, back);
    else if (look.quiver) baldric(c, cx, top, bottom, palette, back);
    else robeCollar(c, cx, top, palette, trim, look, back);
  });

  drawShoulders(ctx, cx, top, shoulder, palette, look, tier, profile);
}

/**
 * O peitoral do Knight: quilha central e duas placas de costela.
 *
 * Placa não é pano — ela não vinca, ela **reflete**. Por isso o detalhe aqui é uma linha
 * clara vertical (a quilha, que é onde a luz bate por ser a parte mais saliente) e não uma
 * prega. É esse brilho reto no meio do peito que separa armadura de camisa a esta distância.
 */
function plateDetail(
  ctx: CanvasRenderingContext2D,
  cx: number,
  top: number,
  bottom: number,
  palette: VocationPalette,
  trim: string,
  back: boolean,
): void {
  roundRect(ctx, cx - 1.2, top + 1, 2.4, bottom - top - 5, 1, celLight(palette.cloth, 0.42));

  if (back) return;

  // Gola de placa: uma barra na base do pescoço, que é onde a armadura de verdade termina.
  roundRect(ctx, cx - 5.5, top - 0.5, 11, 2.6, 1.2, celShadow(palette.cloth, 0.4));
  roundRect(ctx, cx - 5.5, top - 0.5, 11, 1.2, 0.6, trim);

  // Duas costelas em diagonal: dão à placa a curvatura que a silhueta chata do tronco não dá.
  ctx.save();
  ctx.strokeStyle = celShadow(palette.cloth, 0.36);
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(cx - 5.5, top + 5);
  ctx.lineTo(cx - 1.5, top + 7.5);
  ctx.moveTo(cx + 5.5, top + 5);
  ctx.lineTo(cx + 1.5, top + 7.5);
  ctx.stroke();
  ctx.restore();
}

/** Talabarte do Paladin: a correia da aljava, atravessando o peito na diagonal. */
function baldric(
  ctx: CanvasRenderingContext2D,
  cx: number,
  top: number,
  bottom: number,
  palette: VocationPalette,
  back: boolean,
): void {
  const leather = palette.trim[0];

  ctx.save();
  ctx.strokeStyle = leather;
  ctx.lineWidth = 2.4;
  ctx.beginPath();
  // De costas a correia vai para o outro ombro: é o mesmo couro visto do outro lado, e
  // inverter o sentido é o que impede a impressão de que ele deu meia-volta com a aljava.
  ctx.moveTo(cx + (back ? 6 : -6), top + 0.5);
  ctx.lineTo(cx + (back ? -4 : 4), bottom - 3);
  ctx.stroke();
  ctx.strokeStyle = celLight(leather, 0.3);
  ctx.lineWidth = 0.8;
  ctx.stroke();
  ctx.restore();
}

/** Gola da veste: duas lapelas em bico, o V que diz "isto se veste por cima da cabeça". */
function robeCollar(
  ctx: CanvasRenderingContext2D,
  cx: number,
  top: number,
  palette: VocationPalette,
  trim: string,
  look: Look,
  back: boolean,
): void {
  if (back) {
    // De costas, o capuz caído (Druid) ou a gola alta (Sorcerer) fecham a nuca.
    roundRect(ctx, cx - 5, top - 1, 10, 4, 1.8, palette.clothShade);
    return;
  }

  ctx.fillStyle = palette.clothShade;
  ctx.beginPath();
  ctx.moveTo(cx - 5.5, top - 1);
  ctx.lineTo(cx, top + 7);
  ctx.lineTo(cx + 5.5, top - 1);
  ctx.closePath();
  ctx.fill();

  // A fita da gola, e o fecho: no Sorcerer é uma gema, no Druid um nó de corda. É o único
  // ponto em que as duas vestes longas se distinguem de perto.
  ctx.strokeStyle = trim;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(cx - 5, top - 0.5);
  ctx.lineTo(cx - 0.5, top + 6.5);
  ctx.moveTo(cx + 5, top - 0.5);
  ctx.lineTo(cx + 0.5, top + 6.5);
  ctx.stroke();

  if (look.head === 'hat') {
    ellipse(ctx, cx, top + 6.5, 1.8, 1.8, trim);
    ellipse(ctx, cx - 0.5, top + 6, 0.8, 0.8, celLight(trim, 0.6));
    return;
  }
  ellipse(ctx, cx, top + 6.5, 1.6, 1.6, celShadow(palette.trim[0], 0.1));
}

/**
 * O ombro, que é onde o peso da classe aparece.
 *
 * O Knight ganha placa desde a primeira faixa e ela cresce; o Paladin ganha uma só, do lado
 * do arco; os conjuradores ganham uma capa de ombro de pano, que é redonda e mole onde a do
 * Knight é angulosa. Mesma peça, três leituras — e a largura resultante do ombro é o que faz
 * um parecer pesado e o outro leve antes de qualquer detalhe ser visível.
 */
function drawShoulders(
  ctx: CanvasRenderingContext2D,
  cx: number,
  top: number,
  shoulder: number,
  palette: VocationPalette,
  look: Look,
  tier: number,
  profile: boolean,
): void {
  if (tier < 1) return;

  const trim = trimOf(palette, tier);
  const left = cx - shoulder / 2 - 2;
  const right = cx + shoulder / 2 - 3.5;

  if (look.head === 'helm') {
    // Placa angulosa, e maior na última faixa: o ombro do Knight é a peça que mais cresce
    // com o equipamento, porque largura de ombro é como armadura pesada se anuncia.
    const w = tier >= 2 ? 7 : 5.5;
    const pad = (x: number): void => {
      const shape: Shape = (c) => {
        c.beginPath();
        c.moveTo(x, top + 4.5);
        c.quadraticCurveTo(x - 0.5, top - 1.5, x + w / 2, top - 1.8);
        c.quadraticCurveTo(x + w + 0.5, top - 1.5, x + w, top + 4.5);
        c.lineTo(x + w - 1, top + 5.5);
        c.lineTo(x + 1, top + 5.5);
        c.closePath();
      };
      celMass(ctx, shape, trim, { depth: 0.36, light: celLight(trim, 0.55) });
    };
    pad(left - (tier >= 2 ? 1 : 0));
    if (!profile) pad(right - (tier >= 2 ? 0.5 : 0));
    return;
  }

  if (look.robe) {
    // Capa de ombro: uma peça só, cobrindo os dois ombros e a base do pescoço.
    const half = (profile ? shoulder - 1 : shoulder + 2) / 2;
    const shape: Shape = (c) => {
      c.beginPath();
      c.moveTo(cx - half, top + 4);
      c.quadraticCurveTo(cx - half + 0.5, top - 2.5, cx, top - 2.5);
      c.quadraticCurveTo(cx + half - 0.5, top - 2.5, cx + half, top + 4);
      c.lineTo(cx + half * 0.55, top + 6);
      c.lineTo(cx, top + 4.5);
      c.lineTo(cx - half * 0.55, top + 6);
      c.closePath();
    };
    celMass(ctx, shape, tier >= 2 ? trim : palette.clothShade, {
      depth: 0.32,
      light: tier >= 2 ? celLight(trim, 0.4) : palette.cloth,
    });
    return;
  }

  // Paladin: uma ombreira só, de couro, no ombro do arco — assimetria é o que distingue
  // caçador de soldado sem gastar largura.
  const shape: Shape = (c) => roundRectPath(c, right, top - 1, 6, 5.5, 2.4);
  celMass(ctx, shape, tier >= 2 ? trim : palette.trim[0], { depth: 0.34 });
  if (tier >= 2) {
    roundRect(ctx, right + 1, top - 0.5, 4, 1.2, 0.6, celLight(trim, 0.5));
  }
}

/**
 * Manto da terceira faixa.
 *
 * Uma peça só, com a barra em três pontas, e não duas tiras retas de cada lado do corpo —
 * que era o que a versão anterior desenhava e lia como suspensório. O manto é a silhueta
 * mais barata que existe para dizer "este é veterano": ele muda o **contorno** do
 * personagem, e contorno é o que se enxerga antes de qualquer cor.
 */
function drawCloak(
  ctx: CanvasRenderingContext2D,
  cx: number,
  top: number,
  bottom: number,
  palette: VocationPalette,
  profile: boolean,
): void {
  const half = profile ? 6.5 : 9.5;

  const shape: Shape = (c) => {
    c.beginPath();
    c.moveTo(cx - half + 3, top);
    c.quadraticCurveTo(cx - half - 1, top + 6, cx - half, bottom - 2);
    c.lineTo(cx - half * 0.55, bottom);
    c.lineTo(cx - half * 0.2, bottom - 2.5);
    c.lineTo(cx + half * 0.2, bottom);
    c.lineTo(cx + half * 0.55, bottom - 2.5);
    c.lineTo(cx + half, bottom - 1);
    c.quadraticCurveTo(cx + half + 1, top + 6, cx + half - 3, top);
    c.closePath();
  };

  celMass(ctx, shape, palette.clothShade, { depth: 0.3, light: palette.cloth, dx: -2.5, dy: -1 });
}

/**
 * Aljava do Paladin.
 *
 * As flechas passando do ombro são a assinatura de silhueta do arqueiro: três hastes
 * saindo em leque acima da linha do ombro se leem como aljava mesmo quando o corpo inteiro
 * é uma mancha escura contra o fundo, e é a única peça do jogo que aparece **exatamente
 * igual** de frente e de costas — porque é isso que uma coisa nas costas faz.
 */
function drawQuiver(
  ctx: CanvasRenderingContext2D,
  cx: number,
  torsoTop: number,
  palette: VocationPalette,
  direction: Direction,
): void {
  const back = direction === Direction.North;
  const profile = direction === Direction.East || direction === Direction.West;
  // De costas a aljava está inteira à mostra e vale centrá-la; de frente ela tem de ficar
  // **fora da silhueta da cabeça**, senão as flechas nascem atrás do cabelo e a peça inteira
  // desaparece — que foi exatamente o que aconteceu com a primeira posição, a seis pixels do
  // centro: a massa de cabelo é meio pixel mais larga que o crânio e engolia as três hastes.
  const x = back ? cx + 5 : profile ? cx - 1.5 : cx - 8.5;
  const top = torsoTop - 1;
  const leather = palette.trim[0];

  const tube: Shape = (c) => roundRectPath(c, x - 2.2, top, 4.4, 11.5, 1.8);
  celMass(ctx, tube, leather, { depth: 0.36, light: celLight(leather, 0.3) });
  roundRect(ctx, x - 2.2, top + 3.5, 4.4, 1.4, 0.6, celShadow(leather, 0.45));

  // As hastes: três, em leque, com penas na ponta. Duas seriam um par e quatro viram tufo.
  //
  // O leque é feito **girando** cada haste em volta da boca da aljava, e não deslocando-as no
  // eixo x: deslocadas, as três saíam paralelas e tão juntas que o desenho lia como uma tábua
  // — que foi o que aconteceu na primeira tentativa, com as três empilhadas dentro do próprio
  // tubo. Giradas, elas abrem um triângulo de bordas duras acima do ombro, e é o triângulo
  // que se lê como aljava a qualquer distância.
  // A pena da flecha é **clara e fria**, não verde: no verde-claro da roupa as três pontas
  // juntas liam como um tufo de mato saindo do ombro. Penugem de flecha é branca suja, e o
  // contraste contra o couro do tubo é o que separa as hastes umas das outras.
  const vane = '#efe7d4';
  // De costas o leque abre para a direita, para fora da cabeça: centrado, as três hastes
  // nasciam **atrás do crânio** e a aljava sumia justo na direção em que ela deveria ser a
  // peça mais visível de todas.
  const lean = back ? 0.3 : profile ? -0.16 : -0.24;
  for (let i = -1; i <= 1; i++) {
    pivoted(ctx, { x, y: top + 2 }, lean + i * 0.38, (c) => {
      roundRect(c, -0.6, -11, 1.2, 11, 0.5, celShadow(palette.weapon, 0.3));
      roundRect(c, -1.2, -11.8, 2.4, 3.2, 0.9, vane);
      roundRect(c, -1.2, -11.8, 2.4, 1.1, 0.5, celShadow(vane, 0.35));
    });
  }
}

function drawArm(
  ctx: CanvasRenderingContext2D,
  anchor: Point,
  palette: VocationPalette,
  look: Look,
  tier: number,
  back: boolean,
): void {
  // A manga do braço da frente sai na **cor da roupa**, e não na sombra dela: pintada de
  // sombra, ela some contra a faixa escura que o tronco já tem no mesmo lado, e o que
  // sobrava na tela era uma mão flutuando ao lado da cintura sem braço nenhum.
  const cloth = back ? celShadow(palette.cloth, 0.42) : palette.cloth;
  const skin = back ? SKIN_SHADE : SKIN;
  const { x, y } = anchor;

  // Onze pixels, e não nove: o braço tem que chegar ao quadril, porque é lá que uma mão
  // parada fica. Nove deixava as duas mãos na altura da cintura, e duas bolas de pele na
  // cintura leem como alguém carregando alguma coisa.
  //
  // A veste longa tem manga larga: ela abre para baixo em vez de descer reta, e essa abertura
  // é o que faz o braço do conjurador não ser o mesmo braço do guerreiro em outra cor.
  if (look.robe) {
    const sleeve: Shape = (c) => {
      c.beginPath();
      c.moveTo(x + 0.2, y);
      c.lineTo(x - 1.2, y + 9);
      c.quadraticCurveTo(x - 1.4, y + 10.5, x + 0.4, y + 10.5);
      c.lineTo(x + 3.6, y + 10.5);
      c.quadraticCurveTo(x + 5.4, y + 10.5, x + 5.2, y + 9);
      c.lineTo(x + 3.8, y);
      c.closePath();
    };
    celMass(ctx, sleeve, cloth, { depth: 0.3 });
  } else {
    celMass(ctx, capsule(x, y, 4, 11), cloth, { depth: 0.3 });
  }

  // Bracelete/manopla: a partir da segunda faixa o punho ganha metal. É pequeno, mas fica
  // exatamente ao lado da mão, que é onde o olho já está durante o ataque.
  if (tier >= 1 && !look.robe) {
    const trim = trimOf(palette, tier);
    roundRect(ctx, x - 0.4, y + 7.4, 4.8, 2.4, 1, trim);
  }

  // Mão: uma bolinha, e não um quadrado. A mão é pequena demais para ter dedo, mas
  // grande o bastante para o formato importar.
  celMass(ctx, capsule(x - 0.3, y + 9.5, 4.6, 4.2), skin, { depth: 0.3 });
}

function drawHead(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  torsoTop: number,
  palette: VocationPalette,
  look: Look,
  tier: number,
  direction: Direction,
): void {
  const back = direction === Direction.North;
  // Pescoço: sempre na sombra, porque o queixo está por cima dele. Desenhado antes da
  // cabeça para que a curva do queixo o cubra.
  roundRect(ctx, cx - 2, cy + 4, 4, torsoTop - cy - 2, 1.5, SKIN_SHADE);

  // De costas a cabeça também tem massa. Sem esta linha o crânio era um buraco tapado só
  // pelo cabelo — e nas duas classes que trocam cabelo por elmo ou capuz sobrava um chapéu
  // **flutuando** acima de um toco de pescoço, que é o pior defeito que um sprite pode ter
  // justamente na direção em que o jogador mais olha o próprio personagem.
  //
  // E ela é pintada da cor do que a cobre, nunca de pele: as mechas do cabelo terminam em
  // bico, e entre um bico e outro a pele de trás aparecia — dois riscos cor de carne no meio
  // da nuca que liam como bigode.
  if (back) {
    const fill =
      look.head === 'helm'
        ? palette.cloth
        : look.head === 'hood'
          ? palette.clothShade
          : palette.hairShade;
    celMass(ctx, headShape(cx, cy), fill, { depth: 0.26, dx: -1.6, dy: -1 });
  }

  // Elmo e capuz **substituem** o cabelo em vez de se sentarem sobre ele: uma touca de metal
  // por cima de uma massa de cabelo produz uma cabeça de duas alturas, que é o defeito que
  // faz sprite pequeno parecer inchado.
  if (look.head === 'cap' || look.head === 'hat') {
    drawBackHair(ctx, cx, cy, palette, back);
  }

  if (look.head === 'hood') drawHoodBack(ctx, cx, cy, palette, direction);

  if (back) {
    drawHeadgear(ctx, cx, cy, palette, look, tier, direction);
    return;
  }

  const head = headShape(cx, cy);
  celMass(ctx, head, SKIN, { depth: 0.26, dx: -1.6, dy: -1 });

  // Sombra da franja na testa: uma faixa de borda dura logo abaixo do cabelo. É o
  // detalhe que mais aproxima o rosto do desenho japonês depois da própria franja —
  // sem ela a testa fica chapada e o cabelo parece colado por cima.
  clipped(ctx, head, (c) => {
    px(c, cx - 8, cy - 4, 16, 4, celShadow(SKIN, 0.22));
  });

  // Sob o elmo não há franja — há a sombra que a viseira joga na cara, que é mais escura e
  // vai até a linha dos olhos. É ela que faz o rosto parecer estar **dentro** de alguma coisa.
  if (look.head === 'cap' || look.head === 'hat') drawFringe(ctx, cx, cy, palette);
  else clipped(ctx, head, (c) => px(c, cx - 8, cy - 9, 16, 7, celShadow(SKIN, 0.5)));

  if (direction === Direction.South) {
    drawEye(ctx, cx - 5, cy, palette, 1);
    drawEye(ctx, cx + 2, cy, palette, -1);
    // Boca: um traço de dois pixels. Mais que isso, num rosto de 14 px, vira careta.
    px(ctx, cx - 1, cy + 4, 2, 1, celShadow(SKIN, 0.4));
  } else {
    // De perfil, um olho só, empurrado para o lado em que o personagem olha — e o nariz,
    // que só existe de perfil: de frente ele seria um pixel sujo no meio do rosto.
    const side = direction === Direction.East ? 1 : -1;
    drawEye(ctx, direction === Direction.East ? cx + 1 : cx - 4, cy, palette, -side);
    px(ctx, cx + side * 6, cy + 1, 1, 1, celShadow(SKIN, 0.35));
  }

  drawHeadgear(ctx, cx, cy, palette, look, tier, direction);
}

/**
 * O que cada classe usa na cabeça.
 *
 * Este é o traço mais importante do arquivo. Numa janela de 32 × 24 tiles o jogador vê o
 * topo dos personagens antes de ver qualquer outra coisa deles — é a parte que nenhuma
 * parede, nenhum arbusto e nenhum outro corpo tapa. Se a classe tem de ser reconhecível a
 * uma olhada, é aqui que ela é reconhecida.
 */
function drawHeadgear(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  palette: VocationPalette,
  look: Look,
  tier: number,
  direction: Direction,
): void {
  const profile = direction === Direction.East || direction === Direction.West;
  const back = direction === Direction.North;
  const side = direction === Direction.West ? -1 : 1;

  if (look.head === 'helm') drawHelm(ctx, cx, cy, palette, tier, profile, back, side);
  else if (look.head === 'cap') drawCap(ctx, cx, cy, palette, tier, direction);
  else if (look.head === 'hood') drawHoodFront(ctx, cx, cy, palette, tier, profile, back);
  else drawWizardHat(ctx, cx, cy, palette, tier, back, side);
}

/**
 * Elmo do Knight: calota, nasal e crista.
 *
 * Aberto na frente, e não fechado com fresta: um elmo integral apaga os olhos, e olho é o
 * que faz um sprite de 32 px parecer uma pessoa e não uma armadura andando sozinha. O que
 * se ganha aqui é a **testa de metal** com a barra do nasal descendo até a ponta do nariz —
 * lê como elmo aberto, mantém o rosto, e a sombra que ele joga sobre os olhos faz o resto.
 */
function drawHelm(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  palette: VocationPalette,
  tier: number,
  profile: boolean,
  back: boolean,
  side: number,
): void {
  const steel = palette.cloth;
  const trim = trimOf(palette, tier);

  const dome: Shape = (c) => {
    c.beginPath();
    c.moveTo(cx - 8, cy - 1);
    c.bezierCurveTo(cx - 8.6, cy - 12.5, cx + 8.6, cy - 12.5, cx + 8, cy - 1);
    // De frente a aba desce em bico sobre as têmporas e abre no meio para o rosto — é o
    // recorte que separa elmo de tigela. De costas não há rosto para abrir: o elmo fecha
    // até a nuca, porque um vão no meio da parte de trás de um elmo é um buraco.
    if (back) {
      c.lineTo(cx + 6.5, cy + 4.5);
      c.lineTo(cx - 6.5, cy + 4.5);
      c.closePath();
      return;
    }
    c.lineTo(cx + 7, cy + 3.5);
    c.lineTo(cx + 5.5, cy - 1.5);
    c.lineTo(cx - 5.5, cy - 1.5);
    c.lineTo(cx - 7, cy + 3.5);
    c.closePath();
  };

  celMass(ctx, dome, steel, { depth: 0.34, light: celLight(steel, 0.55), dx: -2, dy: -1.4 });

  // Rebite da aba, na cor da faixa: a linha que fecha o metal por baixo.
  clipped(ctx, dome, (c) => {
    roundRect(c, cx - 8, cy - 3.4, 16, 1.6, 0.6, trim);
  });

  if (!back) {
    // Nasal: dois pixels de metal descendo entre os olhos até a altura do nariz.
    roundRect(ctx, cx - 1 + (profile ? side * 3 : 0), cy - 3, 2.2, 6, 0.8, steel);
    roundRect(ctx, cx - 1 + (profile ? side * 3 : 0), cy - 3, 1, 6, 0.5, celLight(steel, 0.4));
  }

  if (tier < 1) return;

  // Crista: uma barbatana correndo do alto da testa à nuca. Na última faixa vira penacho —
  // mais alta, mais larga e na cor do trim, que é o que faz a faixa 2 se anunciar de longe.
  const height = tier >= 2 ? 6.5 : 3.5;
  const crest: Shape = (c) => {
    c.beginPath();
    if (profile || back) {
      // De lado e de costas a crista tem comprimento; de frente ela é uma lâmina fina.
      c.moveTo(cx - 6.5, cy - 8.5);
      c.quadraticCurveTo(cx, cy - 10 - height, cx + 6.5, cy - 8.5);
      c.quadraticCurveTo(cx, cy - 9.5, cx - 6.5, cy - 8.5);
    } else {
      c.moveTo(cx - 1.6, cy - 9);
      c.quadraticCurveTo(cx, cy - 10.5 - height, cx + 1.6, cy - 9);
    }
    c.closePath();
  };
  celMass(ctx, crest, trim, { depth: 0.3, light: celLight(trim, 0.5) });
}

/**
 * Gorro de couro do Paladin, com a pena.
 *
 * Um gorro baixo deixa a franja aparecer embaixo — o caçador é a classe de rosto mais
 * visível do jogo, e é assim que ele fica sendo o mais "gente" dos quatro. Quem carrega a
 * silhueta é a **pena**, que sai para trás e para cima: é uma diagonal fina onde as outras
 * classes têm massa, e diagonal fina não se confunde com nada.
 */
function drawCap(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  palette: VocationPalette,
  tier: number,
  direction: Direction,
): void {
  const leather = palette.trim[0];
  const trim = trimOf(palette, tier);

  const crown: Shape = (c) => {
    c.beginPath();
    c.moveTo(cx - 8, cy - 5.5);
    c.bezierCurveTo(cx - 8.5, cy - 13, cx + 8.5, cy - 13, cx + 8, cy - 5.5);
    c.closePath();
  };
  celMass(ctx, crown, leather, { depth: 0.34, light: celLight(leather, 0.4) });

  // Faixa: a costura que fecha o gorro na testa, e onde a pena se encaixa.
  roundRect(ctx, cx - 8, cy - 6.5, 16, 2.2, 1, celShadow(leather, 0.5));
  roundRect(ctx, cx - 8, cy - 6.5, 16, 1, 0.5, tier >= 1 ? trim : celLight(leather, 0.25));

  // A pena.
  //
  // Curta e **gorda**, e não a lasca comprida da primeira versão: uma diagonal de um pixel
  // de espessura com quinze de comprimento não lê como pena, lê como antena — e o contorno
  // que `outline()` põe em volta come metade dela, deixando na tela um risco escuro saindo
  // da cabeça. Três pixels de largura no talo é o mínimo para a forma sobreviver ao contorno.
  //
  // Ela cai para o lado oposto ao do rosto, e nunca para o mesmo lado das flechas da aljava:
  // duas diagonais saindo do mesmo ombro viram um tufo só.
  const away = direction === Direction.East ? -1 : 1;
  const base = { x: cx + away * 5, y: cy - 7 };
  const tip = { x: cx + away * 11.5, y: cy - 14 };
  const feather: Shape = (c) => {
    c.beginPath();
    c.moveTo(base.x, base.y);
    c.quadraticCurveTo(base.x + away * 5, base.y - 2, tip.x, tip.y);
    c.quadraticCurveTo(base.x + away * 3.5, base.y - 6.5, base.x - away * 1.5, base.y - 1);
    c.closePath();
  };
  const plume = tier >= 2 ? trim : mix(palette.clothLight, '#ffffff', 0.45);
  celMass(ctx, feather, plume, { depth: 0.3, light: celLight(plume, 0.4), dx: -1.2, dy: -1 });

  // A haste, um traço mais escuro no meio: é o que faz a pena ter duas barbas em vez de ser
  // uma lasca de cor.
  ctx.save();
  ctx.strokeStyle = celShadow(plume, 0.4);
  ctx.lineWidth = 0.9;
  ctx.beginPath();
  ctx.moveTo(base.x, base.y - 0.5);
  ctx.quadraticCurveTo(base.x + away * 4.5, base.y - 3.5, tip.x - away, tip.y + 0.5);
  ctx.stroke();
  ctx.restore();
}

/**
 * O capuz do Druid, parte de trás.
 *
 * Desenhado antes da cabeça porque o capuz **envolve**: a parte de trás fica atrás do rosto,
 * a da frente por cima da testa, e é esse sanduíche que dá profundidade. Um capuz de uma
 * camada só fica sempre parecendo um lenço pintado na cabeça.
 */
function drawHoodBack(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  palette: VocationPalette,
  direction: Direction,
): void {
  const back = direction === Direction.North;
  const side = direction === Direction.West ? -1 : 1;
  const profile = direction === Direction.East || direction === Direction.West;

  const shape: Shape = (c) => {
    c.beginPath();
    c.moveTo(cx - 9, cy + 4);
    // O pé do capuz: a ponta cai para trás, e é ela que faz a silhueta do Druid não ser uma
    // bola. Numa direção de perfil, ela aponta para o lado contrário ao do rosto.
    c.bezierCurveTo(
      cx - 9.5 - (profile && side > 0 ? 3 : 0),
      cy - 13,
      cx + 9.5 + (profile && side < 0 ? 3 : 0),
      cy - 13,
      cx + 9,
      cy + 4,
    );
    c.lineTo(cx + 6, cy + 7.5);
    c.lineTo(cx, cy + 6);
    c.lineTo(cx - 6, cy + 7.5);
    c.closePath();
  };

  celMass(ctx, shape, palette.clothShade, { depth: 0.3, light: palette.cloth, dx: -2, dy: -1.4 });

  if (!back) return;

  // De costas o capuz é uma massa chapada; o vinco central é o que devolve o volume.
  clipped(ctx, shape, (c) => {
    roundRect(c, cx - 1, cy - 10, 2, 14, 1, celShadow(palette.clothShade, 0.3));
  });
}

/** A boca do capuz: o arco que passa por cima da testa e sombreia o rosto. */
function drawHoodFront(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  palette: VocationPalette,
  tier: number,
  profile: boolean,
  back: boolean,
): void {
  if (back) return;

  const rim: Shape = (c) => {
    c.beginPath();
    c.moveTo(cx - 8.5, cy + 1);
    c.bezierCurveTo(cx - 9, cy - 12, cx + 9, cy - 12, cx + 8.5, cy + 1);
    c.quadraticCurveTo(cx + 6.5, cy - 6, cx, cy - 6);
    c.quadraticCurveTo(cx - 6.5, cy - 6, cx - 8.5, cy + 1);
    c.closePath();
  };
  celMass(ctx, rim, palette.cloth, { depth: 0.32, light: palette.clothLight, dy: -1.6 });

  if (tier < 2) return;

  // Última faixa: dois chifres de galho saindo do capuz. É a peça mais "druida" do jogo, e
  // custa quatro pixels de altura — a mesma economia da crista do Knight.
  const trim = trimOf(palette, tier);
  ctx.save();
  ctx.strokeStyle = trim;
  ctx.lineWidth = 1.4;
  ctx.lineCap = 'round';
  for (const s of profile ? [1] : [-1, 1]) {
    ctx.beginPath();
    ctx.moveTo(cx + s * 5, cy - 8);
    ctx.quadraticCurveTo(cx + s * 8, cy - 12, cx + s * 6.5, cy - 15);
    ctx.moveTo(cx + s * 7, cy - 11.5);
    ctx.lineTo(cx + s * 9.5, cy - 13);
    ctx.stroke();
  }
  ctx.restore();
}

/**
 * O chapéu de bico do Sorcerer.
 *
 * A peça de silhueta mais forte do jogo: um triângulo alto sobre uma aba larga não se
 * parece com nada mais no mundo — nem com criatura, nem com cenário, nem com as outras três
 * vocações. Ele é a razão de o quadro ter passado de 48 para 56 pixels de altura, e paga o
 * custo sozinho: um Sorcerer é reconhecido de costas, na penumbra da caverna, a meia tela de
 * distância.
 *
 * O bico **verga** para trás em vez de subir reto. Reto ele lê como cone de trânsito; a
 * curva é o que diz "pano com peso", e é ela que faz o chapéu parecer usado por alguém.
 */
function drawWizardHat(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  palette: VocationPalette,
  tier: number,
  back: boolean,
  side: number,
): void {
  const cloth = palette.cloth;
  const trim = trimOf(palette, tier);
  const lean = back ? 0 : -side;
  const height = tier >= 2 ? 20 : 16.5;
  const tipX = cx + lean * 5.5;
  const tipY = cy - height;

  const cone: Shape = (c) => {
    c.beginPath();
    c.moveTo(cx - 6.5, cy - 7.5);
    c.quadraticCurveTo(cx - 5 + lean * 2, cy - 13.5, tipX - 1.2, tipY);
    c.quadraticCurveTo(tipX + 0.6, tipY - 1, tipX + 1.6, tipY + 1.4);
    c.quadraticCurveTo(cx + 5.5 + lean * 2.5, cy - 12.5, cx + 6.5, cy - 7.5);
    c.closePath();
  };
  celMass(ctx, cone, cloth, { depth: 0.36, light: palette.clothLight, dx: -2, dy: -1 });

  // Aba: uma elipse achatada, mais larga que os ombros. É ela que dá o "T" do chapéu — sem
  // aba o cone lê como capuz pontudo, que é coisa de outra classe.
  //
  // Ela fica **acima da linha dos olhos**, e não sobre eles. Descida um pixel e meio, como
  // estava, a aba cobria a franja inteira, e o que sobrava embaixo era uma face de dez pixels
  // sem nada em volta: o rosto perdia a moldura de cabelo que o desenho japonês usa para dar
  // formato à cabeça, e o Sorcerer virava um ovo debaixo de um chapéu.
  const brim: Shape = (c) => {
    c.beginPath();
    c.ellipse(cx, cy - 7.2, 10.5, 3.2, 0, 0, Math.PI * 2);
  };
  celMass(ctx, brim, cloth, { depth: 0.34, light: palette.clothLight, dx: -2, dy: -1.2 });

  // Fita na base do cone, e a gema na última faixa: os dois únicos pontos de trim, porque a
  // silhueta já fez o trabalho e mais cor aqui só sujaria o bico.
  clipped(ctx, cone, (c) => {
    roundRect(c, cx - 7, cy - 10.5, 14, 2.4, 1, trim);
  });
  if (tier >= 2) {
    ellipse(ctx, cx + lean * 1.5, cy - 9.5, 2, 2, celLight(trim, 0.35));
    ellipse(ctx, cx + lean * 1.5 - 0.5, cy - 10, 0.9, 0.9, '#ffffff');
  }
}

/**
 * A massa de cabelo por trás da cabeça.
 *
 * Ela é meio pixel mais larga que o crânio de cada lado, e é essa sobra que **emoldura** o
 * rosto: sem ela a face encosta direto no fundo e o cabelo vira uma touca pousada em cima.
 * As pontas embaixo são o que separa cabelo de capacete — cabelo termina em mecha, não em
 * circunferência.
 */
function drawBackHair(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  palette: VocationPalette,
  back: boolean,
): void {
  const shape: Shape = (c) => {
    c.beginPath();
    c.moveTo(cx - 8.5, cy + 1);
    c.bezierCurveTo(cx - 9.5, cy - 11.5, cx + 9.5, cy - 11.5, cx + 8.5, cy + 1);
    c.lineTo(cx + 7, cy + 5.5);
    c.lineTo(cx + 4.5, cy + 1.5);
    c.lineTo(cx + 1.5, cy + 4.5);
    c.lineTo(cx - 1.5, cy + 4.5);
    c.lineTo(cx - 4.5, cy + 1.5);
    c.lineTo(cx - 7, cy + 5.5);
    c.closePath();
  };

  celMass(ctx, shape, palette.hairShade, { depth: 0.3, dx: -2, dy: -1.2 });

  // De costas não há franja, e sem a faixa de brilho a nuca vira uma mancha chapada — que
  // é justamente a direção em que o jogador passa metade do tempo olhando o personagem.
  //
  // A faixa de trás é mais fina e mais fraca que a da franja, e desce da esquerda para a
  // direita acompanhando a curva do crânio. Reta e da mesma força, ela lia como um par de
  // óculos pousado na cabeça — que é o que dois retângulos claros e paralelos fazem em
  // cima de uma massa escura.
  if (!back) return;

  const soft = mix(palette.hairShine, palette.hair, 0.3);
  clipped(ctx, shape, (c) => {
    roundRect(c, cx - 6.5, cy - 6.5, 5, 1.5, 0.7, soft);
    roundRect(c, cx + 0.5, cy - 5.5, 5, 1.5, 0.7, soft);
  });
}

/**
 * Franja com faixa de brilho.
 *
 * A borda de baixo é uma serra de três bicos: é ela que faz o cabelo cair sobre a testa em
 * mechas em vez de terminar numa linha de tigela. E a faixa clara atravessando a franja é
 * o traço mais reconhecível do desenho japonês — dois segmentos, com uma quebra no meio,
 * porque uma banda contínua lê como aro de metal.
 */
function drawFringe(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  palette: VocationPalette,
): void {
  const shape: Shape = (c) => {
    c.beginPath();
    c.moveTo(cx - 7.5, cy - 2);
    c.bezierCurveTo(cx - 8.5, cy - 12, cx + 8.5, cy - 12, cx + 7.5, cy - 2);
    // A serra: a franja termina em três bicos, e é entre eles que a testa aparece. Os
    // bicos param **acima** da linha dos olhos de propósito — a versão anterior descia
    // até abaixo deles e o rosto sumia debaixo do próprio cabelo.
    c.lineTo(cx + 5, cy - 5);
    c.lineTo(cx + 2.5, cy - 1.5);
    c.lineTo(cx, cy - 5.5);
    c.lineTo(cx - 2.5, cy - 1.5);
    c.lineTo(cx - 5, cy - 5.5);
    c.lineTo(cx - 6, cy - 2);
    c.closePath();
  };

  celMass(ctx, shape, palette.hair, { depth: 0.3, dx: -2, dy: -1.5 });

  clipped(ctx, shape, (c) => {
    roundRect(c, cx - 6.5, cy - 7.5, 5.5, 2.2, 1, palette.hairShine);
    roundRect(c, cx + 1, cy - 8, 5.5, 2.2, 1, palette.hairShine);
  });
}

/**
 * Olho grande, com cílio, esclera, íris e luz.
 *
 * Três pixels de largura por quatro de altura, o que é muito para um sprite de 32 — e é o
 * ponto. Olho grande é a convenção que carrega o estilo; encolhê-lo para "caber melhor"
 * devolve o rosto genérico que a versão anterior tinha com dois pixels de cor.
 *
 * `inner` diz de que lado fica o canto interno do olho, para que a esclera apareça no
 * lado de fora nos dois olhos em vez de nos dois no mesmo lado — que é o que dá o
 * estrabismo de sprite pequeno.
 */
function drawEye(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  palette: VocationPalette,
  inner: number,
): void {
  // O cílio é a linha mais escura do rosto, e não o contorno da cabeça: é ele que dá a
  // direção do olhar num olho de três pixels.
  px(ctx, x, y - 1, 3, 1, OUTLINE);
  px(ctx, x, y, 3, 3, EYE_LIGHT);
  px(ctx, x + (inner > 0 ? 1 : 0), y, 2, 3, palette.eye);
  px(ctx, x + (inner > 0 ? 1 : 0), y + 1, 1, 2, EYE);
  px(ctx, x + (inner > 0 ? 1 : 0), y, 1, 1, EYE_LIGHT);
}

// --- Armas ------------------------------------------------------------------

function drawWeapon(
  ctx: CanvasRenderingContext2D,
  hand: Point,
  palette: VocationPalette,
  look: Look,
  tier: number,
  pose: Pose,
  facing: number,
  profile: boolean,
): void {
  if (look.weapon === 'sword') drawSword(ctx, hand, palette, tier, pose, facing);
  else if (look.weapon === 'bow') drawBow(ctx, hand, palette, tier, pose, facing, profile);
  else drawStaff(ctx, hand, palette, look, tier, pose, facing);
}

/**
 * Espada, presa à mão e girando com o golpe.
 *
 * O ângulo sai de `reach` por uma conta só, e é o que faz a arma **acompanhar** a animação:
 * no recuo ela cai para trás, no avanço ela varre para a frente e para cima, e no retorno
 * ela para no meio do caminho. A alternativa — três desenhos de espada, um por quadro de
 * ataque — foi o que existia antes, e produzia uma arma que aparecia do nada e sumia.
 */
function drawSword(
  ctx: CanvasRenderingContext2D,
  hand: Point,
  palette: VocationPalette,
  tier: number,
  pose: Pose,
  facing: number,
): void {
  const steel = palette.weapon;
  const trim = trimOf(palette, tier);
  // Onze pixels, e não catorze: a lâmina sai da mão a meio corpo de altura, e qualquer coisa
  // mais comprida que isto termina **dentro do chão** na pose parada — a ponta encostava na
  // sombra e a espada parecia fincada no tile.
  const length = tier >= 2 ? 12 : 11;

  // 1,05 rad em repouso é a espada apontada para o chão, ligeiramente à frente — a posição
  // em que um braço cansado a segura, e o ângulo que passa a lâmina por **fora** da perna.
  // O 1,5 do multiplicador é a amplitude do golpe.
  const angle = facing * (1.05 - pose.reach * 1.5);

  pivoted(ctx, hand, angle, (c) => {
    if (facing < 0) c.scale(-1, 1);

    // Lâmina em cápsula com um veio claro no meio: aço tem gume, e o gume é uma linha de
    // luz. Dois tons chapados dariam um pedaço de papelão cinza.
    celMass(c, capsule(1, -1.3, length, 2.6), steel, {
      depth: 0.34,
      light: celLight(steel, 0.5),
      dy: -0.8,
    });
    // Guarda em cruz e punho: é o que separa espada de barra de ferro.
    roundRect(c, 0, -3.4, 2, 6.8, 0.8, trim);
    roundRect(c, -4.4, -1.2, 4.6, 2.4, 1, celShadow(palette.trim[0], 0.3));
    ellipse(c, -4.6, 0, 1.5, 1.5, trim);
  });
}

/**
 * Arco, sempre na mão, com a flecha aparecendo no disparo.
 *
 * O arco fica **de pé** o tempo todo, e não deitado no ataque: um arco na horizontal visto
 * de frente é uma linha, e uma linha não se lê. De pé ele é um "D" de dezesseis pixels, que
 * é a segunda silhueta mais reconhecível do jogo depois do chapéu — e a corda puxando para
 * trás com `reach` conta o disparo inteiro sem precisar mover o arco de lugar.
 */
function drawBow(
  ctx: CanvasRenderingContext2D,
  hand: Point,
  palette: VocationPalette,
  tier: number,
  pose: Pose,
  facing: number,
  profile: boolean,
): void {
  const wood = palette.weapon;
  const trim = trimOf(palette, tier);
  const draw = Math.max(0, pose.reach) * 3.5;
  const half = tier >= 2 ? 10 : 8.5;
  // A barriga do arco encolhe de frente. De perfil o arco é visto de chapa e a curva inteira
  // aparece; de frente ele está quase de perfil para a câmera, e a mesma curva desenhava uma
  // **tábua** de seis pixels de largura ao lado do corpo em vez de um arco.
  const belly = profile ? 5.5 : 4.2;

  ctx.save();
  ctx.translate(hand.x, hand.y);
  ctx.scale(facing, 1);
  ctx.lineCap = 'round';

  // A madeira: um arco que abre na direção do alvo. Desenhado em traço, e não em massa,
  // porque um arco tem dois pixels de espessura e massa nesta escala vira mancha.
  ctx.strokeStyle = celShadow(wood, 0.35);
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(-0.6, -half);
  ctx.quadraticCurveTo(belly, 0, -0.6, half);
  ctx.stroke();
  ctx.strokeStyle = wood;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(-1, -half + 0.5);
  ctx.quadraticCurveTo(belly - 0.8, 0, -1, half - 0.5);
  ctx.stroke();

  // Empunhadura enfaixada, no meio: é o que diz onde a mão segura.
  roundRect(ctx, 0.8, -2.6, 2.4, 5.2, 1, trim);

  // A corda, e o vão de dois pixels entre ela e a madeira.
  //
  // O vão é o arco. Sem ele — com a corda encostada na haste, como estava — as duas viram uma
  // peça só e o que fica na tela é uma tábua vertical ao lado do corpo. É o buraco entre a
  // corda e a madeira que o olho lê como "arco", e não a curva da madeira.
  ctx.strokeStyle = '#f2eee2';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(-1.2, -half);
  ctx.lineTo(-1.2 - draw, 0);
  ctx.lineTo(-1.2, half);
  ctx.stroke();

  if (pose.reach > 0.2) {
    // Flecha encaixada: haste, ponta e pena. Só existe no disparo, que é quando ela deve
    // existir — uma flecha permanente na corda faria o Paladin parecer mirar o tempo todo.
    const nock = -1 - draw;
    roundRect(ctx, nock, -0.6, 12 - draw + pose.reach * 3, 1.2, 0.5, mix(wood, '#ffffff', 0.25));
    ctx.fillStyle = celLight(trim, 0.3);
    ctx.beginPath();
    ctx.moveTo(nock + 12 - draw + pose.reach * 3, -2);
    ctx.lineTo(nock + 15.5 - draw + pose.reach * 3, 0);
    ctx.lineTo(nock + 12 - draw + pose.reach * 3, 2);
    ctx.closePath();
    ctx.fill();
    roundRect(ctx, nock, -1.8, 2.6, 3.6, 1, palette.clothLight);
  }

  ctx.restore();
}

/**
 * Cajado dos conjuradores.
 *
 * Mais alto que o personagem — e é de propósito: a haste passando acima da cabeça é uma
 * vertical que nenhuma outra classe tem, e verticais se contam de relance mesmo num
 * amontoado de corpos. O topo é onde as duas vocações de veste se separam: o Druid termina
 * em galho com folhas, o Sorcerer em orbe.
 */
function drawStaff(
  ctx: CanvasRenderingContext2D,
  hand: Point,
  palette: VocationPalette,
  look: Look,
  tier: number,
  pose: Pose,
  facing: number,
): void {
  const wood = staffWood(palette);
  const top = staffTop(tier);
  const angle = staffAngle(pose, facing);

  pivoted(ctx, hand, angle, (c) => {
    if (facing < 0) c.scale(-1, 1);

    celMass(c, capsule(-1.3, top, 2.6, -top + 6), wood, {
      depth: 0.34,
      light: celLight(wood, 0.4),
      dx: -1.4,
    });
    // Duas amarras de couro na haste: é o que faz a madeira parecer trabalhada e não um cabo
    // de vassoura.
    roundRect(c, -1.6, top + 11, 3.2, 1.6, 0.6, celShadow(wood, 0.5));
    roundRect(c, -1.6, top + 15, 3.2, 1.6, 0.6, celShadow(wood, 0.5));

    if (look.head === 'hood') druidHead(c, top, palette, tier);
    else sorcererHead(c, top, palette, tier);
  });
}

/**
 * Onde termina a haste, medido a partir da mão.
 *
 * Tem de passar **acima da cabeça** — e, no Sorcerer, acima da aba do chapéu. Vinte e três
 * pixels deixavam o orbe na altura do rosto, atrás da aba, e o efeito era um cajado sem
 * ponta: a peça que devia ser a assinatura da classe ficava enterrada no próprio chapéu.
 */
function staffTop(tier: number): number {
  return -(tier >= 2 ? 35 : 33);
}

/**
 * A inclinação da haste.
 *
 * Em repouso ela cai um pouco **para fora**, e essa dúzia de graus resolve dois problemas de
 * uma vez: de frente, tira o orbe de trás da aba do chapéu, onde ele desaparecia; de costas,
 * faz o topo do cajado surgir ao lado da cabeça em vez de ficar inteiro atrás dela. Vertical
 * o cajado ficava escondido exatamente nas duas direções em que ele é a assinatura da classe.
 *
 * Na conjuração ela endireita: bater é gesto de braço, conjurar é gesto de haste plantada — e
 * de quebra o clarão volta para cima da mão, longe da borda do quadro, onde o halo cabe.
 */
function staffAngle(pose: Pose, facing: number): number {
  return facing * (0.13 * (1 - pose.cast) + pose.reach * 0.55);
}

/** Onde fica, no quadro, o alto do cajado — a ponta que brilha. */
function staffTip(hand: Point, tier: number, angle: number): Point {
  const length = -staffTop(tier) + 2;
  return {
    x: hand.x + Math.sin(angle) * length,
    y: hand.y - Math.cos(angle) * length,
  };
}

/**
 * Madeira do cajado.
 *
 * Puxada para o marrom a partir da cor de detalhe da vocação. Sem isso o cajado do Sorcerer
 * saía no roxo acinzentado do `trim[0]` dele e lia como **cano**: madeira é quente, e é a
 * temperatura da cor — não o desenho — que diz de que material é uma haste de dois pixels.
 */
function staffWood(palette: VocationPalette): string {
  return mix(palette.trim[0], '#6a4a2c', 0.55);
}

/** Topo do cajado do Druid: o galho que curva e as folhas que nascem dele. */
function druidHead(
  ctx: CanvasRenderingContext2D,
  top: number,
  palette: VocationPalette,
  tier: number,
): void {
  const wood = staffWood(palette);
  const leaf = palette.weapon;

  ctx.save();
  ctx.strokeStyle = wood;
  ctx.lineWidth = 2.4;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(0, top + 2);
  ctx.quadraticCurveTo(4.5, top - 1, 2.5, top - 5);
  ctx.stroke();
  ctx.restore();

  // Folhas: três lóbulos em volta da curva. Duas leem como orelhas; quatro viram arbusto.
  ellipse(ctx, 4.2, top - 2.5, 2.6, 1.6, leaf);
  ellipse(ctx, -1.4, top - 3.4, 2.2, 1.5, celShadow(leaf, 0.25));
  ellipse(ctx, 1.6, top - 6.5, 1.8, 2.4, celLight(leaf, 0.3));

  if (tier < 2) return;
  ellipse(ctx, 2.4, top - 2.2, 1.4, 1.4, celLight(palette.trim[2], 0.4));
}

/** Topo do cajado do Sorcerer: o orbe, e as garras de metal que o seguram. */
function sorcererHead(
  ctx: CanvasRenderingContext2D,
  top: number,
  palette: VocationPalette,
  tier: number,
): void {
  const trim = trimOf(palette, tier);
  const radius = tier >= 2 ? 3.4 : 2.8;

  // Garras: dois ganchos abraçando o orbe por baixo. Sem elas o orbe parece equilibrado na
  // ponta da vara.
  ctx.save();
  ctx.strokeStyle = trim;
  ctx.lineWidth = 1.6;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(-2.6, top + 2);
  ctx.quadraticCurveTo(-3.2, top - 2, -1.4, top - 3);
  ctx.moveTo(2.6, top + 2);
  ctx.quadraticCurveTo(3.2, top - 2, 1.4, top - 3);
  ctx.stroke();
  ctx.restore();

  ellipse(ctx, 0, top - 2, radius, radius, palette.weapon);
  ellipse(ctx, -radius * 0.3, top - 2 - radius * 0.3, radius * 0.45, radius * 0.45, '#ffffff');
}

/**
 * A luz da conjuração.
 *
 * Quem tem cajado conjura **pelo cajado**: o clarão nasce no orbe ou nas folhas, no alto da
 * haste, e não numa bola solta ao lado do ombro — que era onde ele ficava e fazia a magia
 * parecer um objeto carregado em vez de um efeito produzido. Quem não tem cajado conjura
 * pela mão livre, que é a que sobra.
 */
function drawCastGlow(
  ctx: CanvasRenderingContext2D,
  weaponHand: Point,
  freeHand: Point,
  palette: VocationPalette,
  look: Look,
  tier: number,
  pose: Pose,
  facing: number,
): void {
  const staff = look.weapon === 'staff';
  const radius = 2 + pose.cast * (staff ? 3.5 : 3);
  const glow = palette.trim[2] ?? '#ffffff';

  // Quem tem escudo conjura pela mão da **arma**: a mão livre do Knight está atrás do escudo,
  // e um clarão ali vira uma mancha sobre a chapa em vez de uma magia saindo de alguém.
  const anchor = staff || look.shield ? weaponHand : freeHand;
  const tip = staffTip(anchor, tier, staffAngle(pose, facing));
  const x = staff ? tip.x : anchor.x + facing * 1.5;
  const y = staff ? tip.y : anchor.y - 4;

  ctx.save();
  ctx.globalAlpha *= 0.45;
  ellipse(ctx, x, y, radius + 2.5, radius + 2.5, glow);
  ctx.restore();

  ellipse(ctx, x, y, radius, radius, glow);
  // Núcleo branco deslocado para o alto e para a esquerda: é o que faz o orbe parecer uma
  // esfera de luz em vez de um disco de cor.
  ellipse(ctx, x - radius * 0.28, y - radius * 0.3, radius * 0.45, radius * 0.45, '#ffffff');

  // Fagulhas em volta, na fase do gesto: dão movimento ao quadro do meio, que é onde a
  // conjuração precisa parecer que está acontecendo.
  for (let i = 0; i < 3; i++) {
    const angle = (i / 3) * Math.PI * 2 + pose.cast * 3;
    px(
      ctx,
      Math.round(x + Math.cos(angle) * (radius + 3)),
      Math.round(y + Math.sin(angle) * (radius + 3)),
      1,
      1,
      glow,
    );
  }
}

/** A cor de detalhe da faixa de equipamento. */
function trimOf(palette: VocationPalette, tier: number): string {
  return palette.trim[Math.min(tier, palette.trim.length - 1)] ?? palette.trim[0];
}

/** Desenha o escudo do Knight no braço de trás. */
function drawShield(
  ctx: CanvasRenderingContext2D,
  hand: Point,
  palette: VocationPalette,
  tier: number,
  profile: boolean,
): void {
  const trim = trimOf(palette, tier);
  const face = palette.cloth;
  // De perfil só a borda do escudo aparece: um escudo redondo visto de lado é uma tira, e
  // desenhá-lo redondo nas quatro direções é o erro que faz o braço parecer segurar um prato.
  const half = profile ? 2.2 : 5.2;
  const top = hand.y - 7.5;
  // Meio pixel para fora do braço: centrado na mão, o escudo cobria o peitoral inteiro e
  // apagava a quilha e a gola que fazem a armadura ser armadura.
  const sx = hand.x - (profile ? 0 : 1.2);

  const shape: Shape = (c) => {
    c.beginPath();
    c.moveTo(sx - half, top);
    c.lineTo(sx + half, top);
    c.lineTo(sx + half, top + 7);
    c.quadraticCurveTo(sx + half, top + 11.5, sx, top + 13);
    c.quadraticCurveTo(sx - half, top + 11.5, sx - half, top + 7);
    c.closePath();
  };

  celMass(ctx, shape, face, { depth: 0.36, light: celLight(face, 0.45) });

  clipped(ctx, shape, (c) => {
    // Borda metálica e mancheta: as duas linhas que fazem o escudo ser um objeto montado e
    // não uma placa de cor.
    c.strokeStyle = trim;
    c.lineWidth = 1.6;
    shape(c);
    c.stroke();
    if (profile) return;
    ellipse(c, sx, top + 5.5, 2, 2, trim);
    ellipse(c, sx - 0.6, top + 4.9, 0.8, 0.8, celLight(trim, 0.6));
  });
}

// --- Animações --------------------------------------------------------------

function registerAnimations(scene: Phaser.Scene, vocation: Vocation, tier: number): void {
  const key = characterTextureKey(vocation, tier);

  for (const direction of [
    Direction.North,
    Direction.East,
    Direction.South,
    Direction.West,
  ]) {
    const row = direction * COLUMNS;

    define(scene, characterAnimationKey(vocation, tier, 'idle', direction), key,
      IDLE_FRAMES.map((f) => row + f), 2, -1);

    // A caminhada roda a 12 fps: quatro quadros em ~330ms, próximo da duração real de
    // dois passos. A cena reajusta isso quando a velocidade do personagem muda.
    define(scene, characterAnimationKey(vocation, tier, 'walk', direction), key,
      WALK_FRAMES.map((f) => row + f), 12, -1);

    define(scene, characterAnimationKey(vocation, tier, 'attack', direction), key,
      ATTACK_FRAMES.map((f) => row + f), 14, 0);

    define(scene, characterAnimationKey(vocation, tier, 'cast', direction), key,
      CAST_FRAMES.map((f) => row + f), 10, 0);
  }

  define(scene, `${key}-death`, key,
    [0, 1, 2, 3].map((f) => DEATH_ROW * COLUMNS + f), 6, 0);
}

function define(
  scene: Phaser.Scene,
  animationKey: string,
  textureKey: string,
  frames: number[],
  frameRate: number,
  repeat: number,
): void {
  if (scene.anims.exists(animationKey)) return;

  scene.anims.create({
    key: animationKey,
    frames: frames.map((frame) => ({ key: textureKey, frame })),
    frameRate,
    repeat,
  });
}
